/**
 * mk-hero — the first Component subclass in this port.
 *
 * Later animated sections should copy this shape. Three rules make it survive
 * a theme-editor edit, which is when section-renderer.js morphs the DOM with
 * `childrenOnly: true` and replaces everything inside the section wrapper:
 *
 *   1. Never cache child nodes in instance fields. Read `this.refs.*` inside
 *      the frame callback. A cached node survives morph as a detached element
 *      and you end up animating something no longer in the document.
 *   2. Always unsubscribe in disconnectedCallback(). Subscribing twice without
 *      unsubscribing is the double-attach bug — invisible until the animation
 *      runs at double speed.
 *   3. Gate on visibility with the engine's observe(), not a hand-rolled
 *      IntersectionObserver. `root: null` is wrong whenever the cart drawer is
 *      open, because .page-wrapper--drawer-open applies a margin-right and the
 *      roots stop coinciding.
 *
 * What this drives:
 *   - Confetti parallax, subscribed to the shared rAF loop.
 *   - Pausing the CSS wave animation while the hero is offscreen. The wave
 *     itself is pure CSS; JS only toggles a class.
 */

import { Component } from '@theme/component';
import { subscribe, observe } from '@theme/mk-scroll-engine';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

class MkHero extends Component {
  /** @type {(() => void) | null} */
  #unsubscribe = null;
  /** @type {(() => void) | null} */
  #unobserve = null;

  connectedCallback() {
    super.connectedCallback();

    // Arm the reveal before the observer can fire. .mk-reveal is visible until
    // something adds .is-armed, so if this line never runs the content is
    // simply shown — see the contract in mk-tokens.css. Under reduced motion we
    // never arm, so the content is static and visible from the start.
    this.#armReveal();

    // Start paused/offscreen and let the observer correct it on first callback.
    // If this element is already in view the observer fires immediately, so
    // there is no visible flash of a stopped wave.
    this.#unobserve = observe(this, (isVisible) => {
      this.classList.toggle('mk-hero--offscreen', !isVisible);

      if (isVisible) {
        this.#reveal();
        this.#startParallax();
      } else {
        this.#stopParallax();
      }
    });
  }

  #reduced() {
    return window.matchMedia(REDUCED_MOTION).matches;
  }

  #reveals() {
    return this.querySelectorAll('.mk-reveal');
  }

  #armReveal() {
    if (this.#reduced()) return;
    for (const el of this.#reveals()) el.classList.add('is-armed');
  }

  #reveal() {
    for (const el of this.#reveals()) el.classList.add('is-revealed');
  }

  /**
   * MEASURED, task 4: this does NOT fire in practice.
   *
   * CLAUDE.md prescribes updatedCallback() as the morph-recovery hook. It was
   * instrumented on this element across two scenarios — a morph that destroyed
   * and recreated all children, and one that changed the component's own
   * attribute — and the call count was 0 both times. connectedCallback did not
   * re-fire either, because morph runs with `childrenOnly: true` and the
   * element instance is preserved.
   *
   * So do not make this hook load-bearing. What actually keeps a section
   * correct across a morph is:
   *   1. the element instance surviving, so the subscription stays live and is
   *      not duplicated (verified: subscriber count stays 1);
   *   2. Component's own MutationObserver re-syncing this.refs (verified:
   *      refs point at the newly created nodes);
   *   3. reading this.refs inside the frame callback rather than caching nodes.
   *
   * It is kept because it is the documented hook and may fire for other morph
   * callers (facets, paginated-list, cart items). It must never be the only
   * thing standing between a section and a stale reference.
   */
  updatedCallback() {
    super.updatedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopParallax();
    this.#unobserve?.();
    this.#unobserve = null;
  }

  #startParallax() {
    if (this.#unsubscribe) return;
    if (this.#reduced()) return;

    // Nothing to move — do not hold a subscription or a compositor layer.
    if (!this.#confetti().length) return;

    this.classList.add('mk-hero--animating');
    this.#unsubscribe = subscribe((top) => this.#render(top));
  }

  #stopParallax() {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.classList.remove('mk-hero--animating');
  }

  /**
   * Read fresh every time. `refs.confetti` is populated from `ref="confetti[]"`
   * and is re-resolved by Component after a morph.
   * @returns {HTMLElement[]}
   */
  #confetti() {
    const found = this.refs?.confetti;
    if (!found) return [];
    return Array.isArray(found) ? found : [found];
  }

  /**
   * @param {number} top - scroll position of whichever container is active.
   */
  #render(top) {
    for (const el of this.#confetti()) {
      const speedY = parseFloat(el.dataset.mkParallaxY) || 0;
      const speedX = parseFloat(el.dataset.mkParallaxX) || 0;
      el.style.transform = `translate3d(${top * speedX}px, ${top * speedY}px, 0)`;
    }
  }
}

if (!customElements.get('mk-hero')) {
  customElements.define('mk-hero', MkHero);
}
